import Split from 'split.js';

const extractBackgroundImageUrl = (value) => {
    if (!value || typeof value !== 'string') {
        return '';
    }

    const match = value.match(/url\(["']?([^"')]+)["']?\)/);
    return match ? match[1] : value;
};

export class CodeEditor {
    constructor(editor, opts) {
        this.editor = editor;
        this.$ = editor.$;
        this.pfx = editor.getConfig('stylePrefix');
        this.opts = opts;
        this.canvas = this.findWithinEditor(`.${this.pfx}cv-canvas`);
        this.panelViews = opts.appendTo
            ? this.$(opts.appendTo)
            : this.findWithinEditor(`.${this.pfx}pn-${opts.panelId}`);
        this.isShowing = true;
    }

    findPanel() {
        const pn = this.editor.Panels;
        const id = this.opts.panelId;
        return pn.getPanel(id) || pn.addPanel({ id });
    }

    findWithinEditor(selector) {
        return this.$(selector, this.editor.getEl());
    }

    buildCodeEditor(type) {
        const { editor, opts } = this;

        return editor.CodeManager.createViewer({
            codeName: type === 'html' ? 'htmlmixed' : 'css',
            theme: 'hopscotch',
            readOnly: 0,
            autoBeautify: 1,
            autoCloseTags: 1,
            autoCloseBrackets: 1,
            styleActiveLine: 1,
            smartIndent: 1,
            ...opts.codeViewOptions,
        });
    }

    buildSection(type, codeViewer) {
        const { $, pfx, opts } = this;
        const section = $('<section></section>');
        const btnText = type === 'html' ? opts.htmlBtnText : opts.cssBtnText;
        const cleanCssBtn =
            opts.cleanCssBtn && type === 'css'
                ? `<button class="cp-delete-${type} ${pfx}btn-prim">${opts.cleanCssBtnText}</button>`
                : '';
        section.append(
            $(`
            <div class="codepanel-separator">
                <div class="codepanel-label">${type}</div>
                <div class="cp-btn-container">
                    ${cleanCssBtn}
                    <button class="cp-apply-${type} ${pfx}btn-prim">${btnText}</button>
                </div>
            </div>`)
        );
        const codeViewerEl = codeViewer.getElement();
        codeViewerEl.style.height = 'calc(100% - 30px)';
        section.append(codeViewerEl);
        this.codePanel.append(section);
        return section.get(0);
    }

    buildCodePanel() {
        const { $, editor } = this;
        const panel = this.opts.panelId ? this.findPanel() : 0;
        this.codePanel = $('<div></div>');
        this.codePanel.addClass('code-panel');

        this.htmlCodeEditor = this.buildCodeEditor('html');
        this.cssCodeEditor = this.buildCodeEditor('css');

        const sections = [
            this.buildSection('html', this.htmlCodeEditor),
            this.buildSection('css', this.cssCodeEditor),
        ];

        panel &&
            !this.opts.appendTo &&
            panel.set('appendContent', this.codePanel).trigger('change:appendContent');
        this.opts.appendTo && $(this.opts.appendTo).append(this.codePanel);
        this.updateEditorContents();

        this.codePanel.find('.cp-apply-html').on('click', this.updateHtml.bind(this));

        this.codePanel.find('.cp-apply-css').on('click', this.updateCss.bind(this));

        this.opts.cleanCssBtn &&
            this.codePanel.find('.cp-delete-css').on('click', this.deleteSelectedCss.bind(this));

        Split(sections, {
            direction: 'vertical',
            sizes: [50, 50],
            minSize: 100,
            gutterSize: 1,
            onDragEnd: this.refreshEditors.bind(this),
        });

        editor.on('component:update', () => this.updateEditorContents());
        editor.on('stop:preview', () => {
            if (this.isShowing && !this.opts.preserveWidth) {
                this.canvas.css('width', this.opts.openState.cv);
            }
        });
    }

    showCodePanel() {
        this.isShowing = true;
        this.updateEditorContents();
        this.codePanel.css('display', 'block');
        setTimeout(this.refreshEditors.bind(this), 320);

        if (this.opts.preserveWidth) return;

        this.panelViews.css('width', this.opts.openState.pn);
        this.canvas.css('width', this.opts.openState.cv);
    }

    hideCodePanel() {
        if (this.codePanel) this.codePanel.css('display', 'none');
        this.isShowing = false;

        if (this.opts.preserveWidth) return;

        this.panelViews.css('width', this.opts.closedState.pn);
        this.canvas.css('width', this.opts.closedState.cv);
    }

    refreshEditors() {
        this.htmlCodeEditor.refresh();
        this.cssCodeEditor.refresh();
    }

    captureComponentState(component) {
        if (!component) {
            return null;
        }

        const styles = { ...(component.getStyle?.() || {}) };
        const props = {};

        const traits = component.get('traits');
        traits?.each?.((trait) => {
            if (!trait.get('changeProp')) {
                return;
            }

            const name = trait.get('name');
            const value = component.get(name);

            if (value !== undefined && value !== null && value !== '') {
                props[name] = value;
            }
        });

        const backgroundImage = component.get('background-image');
        if (backgroundImage) {
            props['background-image'] = backgroundImage;
        } else if (styles['background-image']) {
            props['background-image'] = extractBackgroundImageUrl(
                styles['background-image']
            );
        }

        return {
            componentId: component.getId(),
            styles,
            props,
        };
    }

    restoreComponentState(component, state) {
        if (!component || !state) {
            return;
        }

        const target = Array.isArray(component) ? component[0] : component;
        if (!target) {
            return;
        }

        const styles = { ...state.styles };
        const newId = target.getId();

        if (state.componentId && newId && state.componentId !== newId && styles) {
            const cssComposer = this.editor.Css;
            const oldRule = cssComposer.get(`#${state.componentId}`);
            const oldRuleStyles = oldRule?.getStyle?.() || {};

            Object.assign(styles, oldRuleStyles);
        }

        if (Object.keys(styles).length) {
            target.addStyle(styles);
        }

        Object.entries(state.props || {}).forEach(([key, value]) => {
            target.set(key, value);

            if (key === 'background-image') {
                target.trigger(`change:${key}`);
            }
        });

        if (
            target.get('type') === 'image-box' &&
            !target.get('background-image') &&
            styles['background-image']
        ) {
            const backgroundImage = extractBackgroundImageUrl(
                styles['background-image']
            );

            if (backgroundImage) {
                target.set('background-image', backgroundImage);
                target.trigger('change:background-image');
            }
        }
    }

    updateHtml(e) {
        e?.preventDefault();
        const { editor, component } = this;
        let htmlCode = this.htmlCodeEditor.getContent().trim();
        if (!htmlCode || htmlCode === this.previousHtmlCode) return;
        this.previousHtmlCode = htmlCode;

        const preservedState = this.captureComponentState(component);

        let idStyles = '';
        this.cssCodeEditor
            .getContent()
            .split('}\n')
            .filter((el) => Boolean(el.trim()))
            .map((cssObjectRule) => {
                if (!/}$/.test(cssObjectRule)) {
                    return `${cssObjectRule}}`;
                }

                return cssObjectRule;
            })
            .forEach((rule) => {
                if (/^#/.test(rule)) {
                    idStyles += rule;
                }
            });

        htmlCode += `<style>${idStyles}</style>`;

        if (component.attributes.type === 'wrapper') {
            editor.setComponents(htmlCode);
            return;
        }

        const replacedComponents = component.replaceWith(htmlCode);
        const newComponent = Array.isArray(replacedComponents)
            ? replacedComponents[0]
            : replacedComponents;

        this.restoreComponentState(newComponent, preservedState);
        editor.select(newComponent);
    }

    updateCss(e) {
        e?.preventDefault();
        const cssCode = this.cssCodeEditor.getContent().trim();
        if (!cssCode || cssCode === this.previousCssCode) return;
        this.previousCssCode = cssCode;
        this.editor.Css.addRules(cssCode);
    }

    deleteSelectedCss(e) {
        e?.preventDefault();
        const selections = this.cssCodeEditor.editor.getSelections();
        selections.forEach((selection) => this.parseRemove(selection));
        this.cssCodeEditor.editor.deleteH();
    }

    parseRemove(removeCss) {
        const { editor } = this;
        const css = editor.Css;
        css.remove(this.getRules(editor.Parser.parseCss(removeCss)));
    }

    getRules(rules, opts = {}) {
        const { editor } = this;
        const sm = editor.Selectors;
        return rules.map((rule) => {
            const selector = sm.get(rule.selectors);
            const { state, selectorsAdd } = rule;
            const { atRuleType, atRuleParams } = opts;
            return (
                selector &&
                editor.Css.get(selector, state, atRuleParams, {
                    selectorsAdd,
                    atRule: atRuleType,
                })
            );
        });
    }

    updateEditorContents() {
        if (!this.isShowing) return;

        this.component = this.editor.getSelected();
        if (this.component) {
            this.htmlCodeEditor.setContent(this.getComponentHtml(this.component));
            this.cssCodeEditor.setContent(
                this.editor.CodeManager.getCode(this.component, 'css', {
                    cssc: this.editor.Css,
                })
            );
        }
    }

    getComponentHtml(component) {
        const { pfx, opts } = this;
        let result = '';
        const componentEl = component.getEl();

        !opts.clearData && componentEl.classList.remove(`${pfx}selected`);
        const html =
            opts.clearData
                ? component.toHTML()
                : component.attributes.type === 'wrapper'
                  ? componentEl.innerHTML
                  : componentEl.outerHTML;
        !opts.clearData && componentEl.classList.add(`${pfx}selected`);
        result += html;

        const js = opts.editJs ? component.getScriptString() : '';
        result += js ? `<script>${js}</script>` : '';

        return result;
    }
}
